import { HIL_DIRECTIVE_TABLES } from './hil-directive-tables.js';

export interface HilDirectiveToolCallProps {
	/** Name of the `hilDirective_*` tool that was called. */
	toolName: string;
	/** Arguments the tool was called with; the table rows are read from these. */
	args: unknown;
}

/**
 * Renders a `hilDirective_show*Table` tool call as its interactive review table.
 * The table is built from the call's arguments, so it renders identically for
 * live streams and reloaded history — no restating by the model required.
 *
 * @param props - The tool call's name and arguments.
 * @returns The table, or null when the tool is unknown or the rows are missing.
 */
export function HilDirectiveToolCall({ toolName, args }: HilDirectiveToolCallProps) {
	const table = HIL_DIRECTIVE_TABLES[toolName];
	if (!table) {
		return null;
	}

	const rows = readRows(args, table.argKey);
	if (rows === null) {
		return null;
	}

	return <table.Item jsonData={JSON.stringify(rows)} />;
}

/**
 * Extracts the row array from a tool call's arguments.
 *
 * @param args - The raw tool-call arguments.
 * @param argKey - Property expected to hold the row array.
 * @returns The rows, or null when absent or not a non-empty array.
 */
function readRows(args: unknown, argKey: string): unknown[] | null {
	if (typeof args !== 'object' || args === null) {
		return null;
	}
	const rows = (args as Record<string, unknown>)[argKey];
	return Array.isArray(rows) && rows.length > 0 ? rows : null;
}
